import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import '../styles/ContactPicker.css';

function ContactPicker({ isVisible, onContactSelect, onClose, user }) {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  // Cache of privacy-filtered profile data
  const [privacyProfiles, setPrivacyProfiles] = useState({});

  // Fetch user's contacts when component becomes visible
  useEffect(() => {
    if (isVisible && user) {
      fetchContacts();
    }
  }, [isVisible, user]);

  // Update filtered contacts when contacts change
  useEffect(() => {
    filterContacts(searchQuery);
  }, [contacts]);

  // Reset search when component becomes visible
  useEffect(() => {
    if (isVisible) {
      setSearchQuery('');
      setSelectedContact(null);
    }
  }, [isVisible]);

  // Fetch privacy-aware profiles for visible contacts
  useEffect(() => {
    if (!isVisible) return; // only fetch when picker is open
    if (!user?.id) return;
    if (!filteredContacts || filteredContacts.length === 0) return;
    const toFetch = filteredContacts
      .map(c => c.id)
      .filter(id => !privacyProfiles[id]);
    if (toFetch.length === 0) return;
    const batch = toFetch.slice(0, 30); // limit batch size
    batch.forEach(id => {
      axios.get(`${config.apiUrl}/api/auth/profile/${id}?requestingUserId=${user.id}`)
        .then(res => {
          setPrivacyProfiles(prev => ({ ...prev, [id]: res.data }));
        })
        .catch(err => {
          console.error('Failed to fetch privacy profile for contact picker user', id, err);
          setPrivacyProfiles(prev => ({ ...prev, [id]: { profileImage: null } }));
        });
    });
  }, [isVisible, filteredContacts, user, privacyProfiles]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.apiUrl}/api/contacts/${user.id}`);
      setContacts(response.data);
      setFilteredContacts(response.data); // Initialize filtered contacts
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter contacts based on search query
  const filterContacts = (query) => {
    if (!query.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    
    const filtered = contacts.filter(contact =>
      contact.username.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredContacts(filtered);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterContacts(query);
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
  };

  const handleSendContact = () => {
    if (selectedContact && onContactSelect) {
      onContactSelect(selectedContact);
      setSelectedContact(null);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedContact(null);
    setSearchQuery('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="contact-picker-overlay">
      <div className="contact-picker">
        <div className="contact-picker-header">
          <h3>Share Contact</h3>
          <button className="close-btn" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Search input */}
        {!loading && contacts.length > 0 && (
          <div className="search-container">
            <input
              type="text"
              placeholder="Search contacts by username..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
        )}

        <div className="contact-picker-content">
          {loading ? (
            <div className="loading-state">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
              </svg>
              <p>Loading contacts...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <p>No contacts available to share</p>
            </div>
          ) : filteredContacts.length === 0 && searchQuery ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              <p>No contacts found matching "{searchQuery}"</p>
            </div>
          ) : (
            <div className="contacts-list">
              {filteredContacts.map(contact => (
                <div 
                  key={contact.id} 
                  className={`contact-item ${selectedContact?.id === contact.id ? 'selected' : ''}`}
                  onClick={() => handleContactSelect(contact)}
                >
                  <div className="contact-avatar">
                    {(() => {
                      const privacyImage = privacyProfiles[contact.id]?.profileImage || null;
                      if (privacyImage) {
                        return (
                          <img
                            src={`${config.apiUrl}${privacyImage}`}
                            alt={contact.username}
                            className="contact-profile-image"
                          />
                        );
                      }
                      return (
                        <div className="contact-avatar-placeholder notranslate">
                          {contact.username.charAt(0).toUpperCase()}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="contact-info">
                    <div className="contact-name notranslate">{contact.username}</div>
                    <div className="contact-status">Contact</div>
                  </div>
                  {selectedContact?.id === contact.id && (
                    <div className="selected-indicator">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedContact && (
          <div className="contact-picker-footer">
            <button className="cancel-btn" onClick={handleClose}>
              Cancel
            </button>
            <button className="send-btn" onClick={handleSendContact}>
              Share Contact
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactPicker;
